import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Slider, Chip, Divider, TextField, Switch, FormControlLabel, useMediaQuery, useTheme } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import Tips from './dialogs/Tips';
import StyledTextField from './common/StyledTextField';
import ReviewPreviewDialog from './dialogs/ReviewPreviewDialog';
import { AnimeReviewData } from '../types/Anime';
import { setAnimeReview } from '../services/animeApi';
import { getUserBirthDate } from '../services/api';
import { animeScoreTags } from '../utils/animeScoreTags';
import LifeStageSelector from './LifeStageSelector';

interface AnimeReviewDialogProps {
  open: boolean;
  onClose: () => void;
  malId: number;
  initialData: AnimeReviewData;
  animeType?: string | null;
  totalEpisodes?: number | null;
  airedFrom?: string | null;
  onReviewSaved?: (reviewData: AnimeReviewData) => void;
}

const DETAIL_SCORE_FIELDS = [
  { key: 'scoreStory' as const, label: 'Story' },
  { key: 'scoreAnimation' as const, label: 'Animation' },
  { key: 'scoreSound' as const, label: 'Sound' },
  { key: 'scoreCharacter' as const, label: 'Character' },
  { key: 'scoreDirection' as const, label: 'Direction' },
];

const WATCH_MEDIUM_MOVIE = [
  { id: 1, label: '劇場' },
  { id: 2, label: 'リバイバル上映' },
  { id: 3, label: 'TV放送' },
  { id: 4, label: 'ストリーミング' },
  { id: 5, label: 'パッケージ' },
];

const WATCH_MEDIUM_TV = [
  { id: 3, label: 'TV放送' },
  { id: 4, label: 'ストリーミング' },
  { id: 5, label: 'パッケージ' },
];

const WATCH_STYLE_OPTIONS = [
  { id: 1, label: 'リアルタイム' },
  { id: 2, label: '再放送' },
  { id: 3, label: '一気見' },
  { id: 4, label: '断片視聴' },
];

const AnimeReviewDialog: React.FC<AnimeReviewDialogProps> = ({
  open,
  onClose,
  malId,
  initialData,
  animeType,
  totalEpisodes,
  airedFrom,
  onReviewSaved,
}) => {
  const [tempData, setTempData] = useState<AnimeReviewData>(initialData);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [userBirthDate, setUserBirthDate] = useState<string | null>(null);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // initialDataが変わったらtempDataを更新 + birthDate取得
  React.useEffect(() => {
    if (open) {
      setTempData(initialData);
      getUserBirthDate().then(setUserBirthDate);
    }
  }, [open, initialData]);

  const scoreMarks = [
    { value: 1, label: '1.0' },
    { value: 2, label: 'poor' },
    { value: 3, label: '3.0' },
    { value: 4, label: 'good' },
    { value: 5, label: '5.0' },
  ];

  const detailScoreMarks = [
    { value: 1 },
    { value: 2 },
    { value: 3 },
    { value: 4 },
    { value: 5 },
  ];

  const handleScoreChange = (_event: Event, newValue: number | number[]) => {
    setTempData({ ...tempData, score: newValue as number });
  };

  const handleDetailScoreChange = (field: keyof AnimeReviewData, _event: Event, newValue: number | number[]) => {
    setTempData({ ...tempData, [field]: newValue as number });
  };

  const handleScoreTagsChange = (tagId: number) => {
    const newTags = tempData.scoreTags.includes(tagId)
      ? tempData.scoreTags.filter((id) => id !== tagId).sort((a, b) => a - b)
      : tempData.scoreTags.length >= 3
        ? tempData.scoreTags
        : [...tempData.scoreTags, tagId].sort((a, b) => a - b);
    setTempData({ ...tempData, scoreTags: newTags });
  };

  const handleSave = async () => {
    try {
      const isChanged = JSON.stringify(tempData) !== JSON.stringify(initialData);
      if (isChanged) {
        await setAnimeReview(malId, tempData);
      }
      onReviewSaved?.(tempData);
      onClose();
    } catch (error) {
      console.error('レビューの保存に失敗しました', error);
    }
  };

  const getScoreText = (score: number | null | undefined) => {
    if (score === null || score === undefined) return 'N/A';
    const numericScore = typeof score === 'string' ? parseFloat(score) : score;
    return isNaN(numericScore) ? '未評価' : numericScore.toFixed(1);
  };

  const getScoreColor = (score: number | null) => {
    if (score === null) return "#666666";
    if (score >= 4.5) return "#4CAF50";
    if (score >= 4.0) return "#8BC34A";
    if (score >= 3.5) return "#CDDC39";
    if (score >= 3.0) return "#FFEB3B";
    if (score >= 2.5) return "#FFC107";
    if (score >= 2.1) return "#FF9800";
    return "#F44336";
  };

  const getDetailScoreStyle = (score: number | null | undefined) => {
    const hasScore = score !== null && score !== undefined;
    return {
      '& .MuiSlider-thumb': { width: 12, height: 12, color: hasScore ? 'primary.main' : 'grey.500' },
      '& .MuiSlider-track': { color: hasScore ? 'primary.main' : 'grey.500' },
      '& .MuiSlider-rail': { color: hasScore ? 'primary.light' : 'grey.300' },
    };
  };

  const scoreCorrectionTips = (
    <Box>
      <Typography variant="body2" paragraph>
        ・スコア補正とは、あなたの評価に大きく影響を与えている可能性のある要因を示すものです。
      </Typography>
      <Typography variant="body2" paragraph>
        ・確信がなくても構いません。「もしかしたら」という程度の直感も大切です。
      </Typography>
      <Typography variant="body2" paragraph>
        ・自分では気づきにくいバイアスもあります。他ユーザーとの評価に乖離を感じる状況などで自問し、思い当たる点があれば選択してください。
      </Typography>
      <Typography variant="body2" paragraph>
        ・これはスコアの妥当性を示すものではなく、あなたの評価の背景や視聴体験を他のユーザーに表明するためのものです。
      </Typography>
      <Typography variant="body2" paragraph>
        ・全てのレビューで選択する必要はありません。スコアに大きく関与している可能性がある場合でのみ設定してください。
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Chip label="制作補正" color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="body2">
            制作スタジオや監督・脚本家など制作陣に関する評価要因です。
            スタジオへの愛着や信頼、または過去の実績・評判が作品内容とは別に評価を左右する可能性を考慮します。
          </Typography>
        </Box>
        <Box>
          <Chip label="原作補正" color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="body2">
            原作（漫画・ラノベ・ゲーム等）を知っているかどうかが評価に影響している可能性を示します。
            原作ファンとしての期待・失望、原作との比較による加点・減点など。
          </Typography>
        </Box>
        <Box>
          <Chip label="声優補正" color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="body2">
            好きな声優・苦手な声優がキャスティングされていることが評価に影響している可能性を示します。
            声優の演技力そのものの評価ではなく、キャストへの個人的な好みによるバイアスを指します。
          </Typography>
        </Box>
        <Box>
          <Chip label="コンテクスト補正" color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="body2">
            作品を取り巻く背景や状況を考慮に入れた評価要因です。
            社会的な話題性、放送当時の時代背景、他作品との比較など、作品内容とは直接関係しない文脈が評価に影響している可能性を考慮します。
          </Typography>
        </Box>
        <Box>
          <Chip label="エントリー補正" color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="body2">
            初めて触れるジャンルやシリーズであることが評価に影響している可能性を示します。
            新鮮さや驚きによる高評価、または不慣れによる低評価など、経験値の差が評価に影響する傾向があります。
          </Typography>
        </Box>
        <Box>
          <Chip label="期待値補正" color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="body2">
            事前の期待値と実際の視聴体験のギャップが評価に影響している可能性を示します。
            高い期待からの失望や、予想以上の出来への驚きなど、期待と実体験の落差が評価に反映されることがあります。
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const reviewFormatTips = (
    <Box>
      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.2 }}>
        Tips：コードをクリックでクリップボードにコピーできます
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[
          { title: '見出し', examples: [{ syntax: '[h1]ヘッダー[/h1]', result: 'ヘッダー' }, { syntax: '[h2]ヘッダー[/h2]', result: 'ヘッダー' }] },
          { title: 'テキスト装飾', examples: [{ syntax: '[b]太字[/b]', result: '太字' }, { syntax: '[i]斜体[/i]', result: '斜体' }, { syntax: '[s]取り消し線[/s]', result: '取り消し線' }, { syntax: '[u]下線[/u]', result: '下線' }] },
          { title: 'リスト', examples: [{ syntax: '[list]\n[*]リスト項目\n[*]リスト項目\n[/list]', result: '箇条書き' }, { syntax: '[olist]\n[*]リスト項目\n[*]リスト項目\n[/olist]', result: '番号付きリスト' }] },
          { title: 'その他', examples: [{ syntax: '[quote]引用文[/quote]', result: '引用ブロック' }, { syntax: '[spoiler]ネタバレ[/spoiler]', result: 'ネタバレ' }] },
        ].map((section) => (
          <Box key={section.title}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>{section.title}</Typography>
            {section.examples.map((example, index) => (
              <Box key={index} sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, mb: 0.5, '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.03)' } }}>
                <Box component="code" onClick={() => navigator.clipboard.writeText(example.syntax)}
                  sx={{ bgcolor: 'rgba(0, 0, 0, 0.3)', p: 0.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.65rem', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.4)' } }}>
                  {example.syntax}
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{example.result}</Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose()}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle>アニメステータス</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            {/* 総合スコア */}
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ lineHeight: 1 }}>スコア</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{
                    color: "white", fontSize: "0.9rem", fontWeight: '600',
                    py: 0, px: 1.25, borderRadius: 1.5,
                    backgroundColor: `${getScoreColor(tempData.score)}B3`, textAlign: 'center'
                  }}>
                    {getScoreText(tempData.score)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1 }}>
                    基準値：3.0=普通
                  </Typography>
                </Box>
              </Box>
              <Slider
                value={tempData.score ?? 3}
                onChange={handleScoreChange}
                aria-labelledby="anime-score-slider"
                step={0.1}
                marks={scoreMarks}
                min={1}
                max={5}
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-thumb': {
                    color: tempData.score !== null && tempData.score !== undefined ? 'primary.main' : 'grey.500',
                    width: 18, height: 18,
                  },
                  '& .MuiSlider-track': {
                    color: tempData.score !== null && tempData.score !== undefined ? 'primary.main' : 'grey.500',
                    height: 3,
                  },
                  '& .MuiSlider-rail': {
                    color: tempData.score !== null && tempData.score !== undefined ? 'primary.light' : 'grey.300',
                    height: 3,
                  },
                }}
              />
            </Box>

            {/* 詳細スコア */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography>評価項目</Typography>
                <Tips
                  title="Anime Rating Standards"
                  maxWidth="sm"
                  buttonComponent={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <HelpOutlineIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                      <Typography variant="body2" sx={{
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                        cursor: 'pointer', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1,
                        '&:hover': { textDecoration: 'underline', color: 'rgba(255, 255, 255, 0.9)' }
                      }}>
                        スコアガイドを見る
                      </Typography>
                    </Box>
                  }
                  content={
                    <Box>
                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>ストーリー</Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {`・脚本・構成（物語の構成力、展開の巧みさ）
・テーマ性（メッセージの深さ、一貫性）
・伏線・回収（伏線の張り方と回収の満足度）`}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                        物語全体の構成と展開を評価します。ストーリーの引き込む力、テーマの深さ、伏線の活用、結末の納得感を総合的に判断してください。
                      </Typography>

                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>作画</Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {`・作画クオリティ（キャラデザ、背景美術）
・アニメーション（動きの滑らかさ、アクション作画）
・ビジュアル表現（色彩設計、特殊効果）`}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                        映像面の品質を評価します。静止画の美しさだけでなく、動きの表現力やアートスタイルの一貫性、視覚的な演出効果も含めて判断してください。
                      </Typography>

                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>サウンド</Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {`・音楽（BGM、OP/ED楽曲の質・印象）
・声優演技（キャスティング、演技力）
・音響演出（SE、環境音、音響効果）`}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                        聴覚体験全般を評価します。音楽が作品の雰囲気を盛り上げているか、声優の演技がキャラクターに命を吹き込んでいるか、音響演出が没入感を高めているかを判断してください。
                      </Typography>

                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>キャラクター</Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {`・魅力（キャラクターの個性、デザイン）
・描写（内面の掘り下げ、成長・変化）
・関係性（キャラクター間のダイナミクス）`}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                        登場人物の魅力と描写を評価します。キャラクターが生き生きと描かれているか、感情移入できるか、成長や関係性の変化が丁寧に描かれているかを判断してください。
                      </Typography>

                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>演出</Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {`・カメラワーク・テンポ（場面転換、間の取り方）
・世界観構築（舞台設定の作り込み、雰囲気）
・没入感（作品に引き込まれる力、感情体験）`}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#dadada' }}>
                        演出技法と作品全体の没入感を評価します。カメラワークやテンポの巧みさ、世界観の構築力、そして総合的に「作品に引き込まれるか」を判断してください。
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ display: 'grid', gap: 0.5 }}>
                {DETAIL_SCORE_FIELDS.map(({ key, label }) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="body2" sx={{ minWidth: '80px', fontSize: '0.8rem', color: 'text.secondary' }}>
                      {label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Slider
                        value={tempData[key] ?? 3}
                        onChange={(event, newValue) => handleDetailScoreChange(key, event, newValue)}
                        step={0.1}
                        min={1}
                        max={5}
                        marks={detailScoreMarks}
                        valueLabelDisplay="auto"
                        size="small"
                        sx={{
                          flex: 1,
                          '@media (pointer: coarse)': { py: 1.75 },
                          ...getDetailScoreStyle(tempData[key])
                        }}
                      />
                      <Typography variant="caption" sx={{
                        minWidth: '30px', textAlign: 'center', color: "white",
                        fontSize: "0.7rem", fontWeight: '500', lineHeight: 1,
                        py: 0.25, px: 0.75, borderRadius: 1,
                        backgroundColor: `${getScoreColor(tempData[key])}B3`,
                      }}>
                        {getScoreText(tempData[key])}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* 視聴話数 */}
          {animeType !== 'Movie' && (
            <Box sx={{ my: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography>視聴話数</Typography>
                <Typography variant="body2" color="text.secondary">
                  {tempData.episodesWatched ?? '—'}{totalEpisodes ? ` / ${totalEpisodes}話` : '話'}
                </Typography>
              </Box>
              {totalEpisodes ? (
                <Box>
                  <Slider
                    value={tempData.episodesWatched ?? 0}
                    onChange={(_e, v) => setTempData({ ...tempData, episodesWatched: (v as number) || null })}
                    step={1}
                    min={0}
                    max={totalEpisodes}
                    valueLabelDisplay="auto"
                    size="small"
                    sx={{
                      '& .MuiSlider-thumb': { width: 14, height: 14 },
                      '& .MuiSlider-track': { height: 3 },
                      '& .MuiSlider-rail': { height: 3 },
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <Button
                      size="small" variant="outlined"
                      disabled={(tempData.episodesWatched ?? 0) <= 0}
                      onClick={() => setTempData({ ...tempData, episodesWatched: Math.max(0, (tempData.episodesWatched ?? 0) - 1) || null })}
                      sx={{ minWidth: 56, px: 1, py: 0.25, fontSize: '0.7rem', lineHeight: 1.2, borderColor: 'rgba(255,255,255,0.23)', color: 'text.primary' }}
                    >
                      −1
                    </Button>
                    <Button
                      size="small" variant="outlined"
                      disabled={(tempData.episodesWatched ?? 0) >= totalEpisodes}
                      onClick={() => setTempData({ ...tempData, episodesWatched: Math.min(totalEpisodes, (tempData.episodesWatched ?? 0) + 1) })}
                      sx={{ minWidth: 56, px: 1, py: 0.75, fontSize: '0.7rem', lineHeight: 1.2, borderColor: 'rgba(255,255,255,0.23)', color: 'text.primary' }}
                    >
                      +1
                    </Button>
                    <Button
                      size="small" variant="outlined"
                      onClick={() => setTempData({ ...tempData, episodesWatched: totalEpisodes })}
                      sx={{ minWidth: 56, px: 1.5, py: 0.25, fontSize: '0.65rem', lineHeight: 1.2, borderColor: 'rgba(255,255,255,0.23)', color: 'text.primary' }}
                    >
                      ALL
                    </Button>
                  </Box>
                </Box>
              ) : (
                <TextField
                  type="number"
                  size="small"
                  value={tempData.episodesWatched ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTempData({ ...tempData, episodesWatched: v === '' ? null : Math.max(0, parseInt(v)) });
                  }}
                  inputProps={{ min: 0 }}
                  sx={{ width: 120 }}
                />
              )}
            </Box>
          )}

          {/* 視聴媒体 */}
          <Box sx={{ my: 2 }}>
            <Typography sx={{ mb: 0.5 }}>視聴媒体</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {(animeType === 'Movie' ? WATCH_MEDIUM_MOVIE : WATCH_MEDIUM_TV).map((opt) => (
                <Chip
                  key={opt.id}
                  label={opt.label}
                  size="small"
                  color={tempData.watchMedium.includes(opt.id) ? 'primary' : 'default'}
                  onClick={() => {
                    const arr = tempData.watchMedium.includes(opt.id)
                      ? tempData.watchMedium.filter((id) => id !== opt.id)
                      : [...tempData.watchMedium, opt.id];
                    setTempData({ ...tempData, watchMedium: arr.sort((a, b) => a - b) });
                  }}
                  sx={{ '& .MuiChip-label': { fontSize: '0.7rem' } }}
                />
              ))}
            </Box>
          </Box>

          {/* 視聴形態 (TV系のみ) */}
          {animeType !== 'Movie' && (
            <Box sx={{ my: 2 }}>
              <Typography sx={{ mb: 0.5 }}>視聴形態</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {WATCH_STYLE_OPTIONS.map((opt) => (
                  <Chip
                    key={opt.id}
                    label={opt.label}
                    size="small"
                    color={tempData.watchStyle.includes(opt.id) ? 'primary' : 'default'}
                    onClick={() => {
                      const arr = tempData.watchStyle.includes(opt.id)
                        ? tempData.watchStyle.filter((id) => id !== opt.id)
                        : [...tempData.watchStyle, opt.id];
                      setTempData({ ...tempData, watchStyle: arr.sort((a, b) => a - b) });
                    }}
                    sx={{ '& .MuiChip-label': { fontSize: '0.7rem' } }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* 日付・再視聴 */}
          <Box sx={{ my: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
              <TextField
                label="視聴開始日"
                type="date"
                size="small"
                value={tempData.startDate || ''}
                onChange={(e) => setTempData({ ...tempData, startDate: e.target.value || null })}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <LifeStageSelector
                birthDate={userBirthDate}
                targetDate={airedFrom || undefined}
                targetDateLabel="放送開始日"
                onSelect={(date) => setTempData({ ...tempData, startDate: date })}
                mode="start"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
              <TextField
                label="視聴完了日"
                type="date"
                size="small"
                value={tempData.endDate || ''}
                onChange={(e) => setTempData({ ...tempData, endDate: e.target.value || null })}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <LifeStageSelector
                birthDate={userBirthDate}
                targetDate={airedFrom || undefined}
                targetDateLabel="放送開始日"
                onSelect={(date) => setTempData({ ...tempData, endDate: date })}
                mode="end"
                startDate={tempData.startDate}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={tempData.rewatch}
                  onChange={(e) => setTempData({ ...tempData, rewatch: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography variant="body2">再視聴済み</Typography>}
            />
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* レビューテキスト */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: -2 }}>
            <Button
              color="primary"
              size="small"
              onClick={() => setPreviewOpen(true)}
              disabled={!tempData.reviewText}
              sx={{ fontSize: { xs: "0.55rem", sm: "0.6rem" }, minWidth: 'auto', p: '2px 8px' }}
            >
              プレビュー
            </Button>
            <Tips
              title="テキストフォーマット"
              content={reviewFormatTips}
              buttonComponent={
                <Typography variant="body2" color="primary" sx={{
                  fontSize: { xs: "0.55rem", sm: "0.6rem" },
                  cursor: 'pointer', '&:hover': { textDecoration: 'underline' }
                }}>
                  使用可能フォーマット
                </Typography>
              }
            />
          </Box>
          <Box sx={{ mt: 0.25, mb: 2 }}>
            <StyledTextField
              id="review"
              label="レビュー"
              value={tempData.reviewText || ''}
              onChange={(value) => setTempData({ ...tempData, reviewText: value })}
              multiline
              minRows={fullScreen ? 6 : 4}
              maxRows={12}
              placeholder="レビューを入力してください"
            />
          </Box>

          {/* スコア補正タグ */}
          <Box sx={{ my: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography>スコア補正</Typography>
              <Tips title="スコア補正について" content={scoreCorrectionTips} maxWidth="sm"/>
              <Typography color="text.secondary" variant="body2" sx={{ ml: 1 }}>
                {tempData.scoreTags.length}/3
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {animeScoreTags.map((tag) => (
                <Chip
                  key={tag.id}
                  icon={
                    tag.id === 9 || tag.id >= 30 ? (
                      <KeyboardDoubleArrowDownIcon style={{ color: '#79bcf3' }} />
                    ) : (
                      <KeyboardDoubleArrowUpIcon style={{ color: '#f37979' }} />
                    )
                  }
                  label={tag.label}
                  onClick={() => handleScoreTagsChange(tag.id)}
                  color={tempData.scoreTags.includes(tag.id) ? 'primary' : 'default'}
                  size="small"
                  disabled={!tempData.scoreTags.includes(tag.id) && tempData.scoreTags.length >= 3}
                  sx={{
                    '& .MuiChip-icon': { fontSize: '0.7rem' },
                    '& .MuiChip-label': { fontSize: '0.6rem' }
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ pb: fullScreen ? 'calc(8px + env(safe-area-inset-bottom))' : undefined }}>
          <Button onClick={handleSave} color="primary" variant="contained">保存</Button>
          <Button onClick={() => onClose()} color="inherit">キャンセル</Button>
        </DialogActions>
      </Dialog>

      <ReviewPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        reviewText={tempData.reviewText || ''}
      />
    </>
  );
};

export default AnimeReviewDialog;
